import React, { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { updateMyPassword } from '../../api/services/meService';
import { ResponseStatus } from '../../components/notifications';
import { FieldInput } from '../../components/fieldInput';
import cx from 'classnames';
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import { DataResponse } from '../../api/responses/baseResponse';
import { useAppSelector } from '../../app/hooks';
import { checkPreviousPasswords } from '../../api/services/authService';
import CheckPreviousPasswordModel from '../../api/models/checkPreviousPasswordModel';

type Rule = {
  text: string;
  validation: () => boolean;
};

const ChangePassword = () => {
  // State
  const [currentPw, setcurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const userState = useAppSelector((userState) => userState);
  const [isAlreadyBeenUsed, setIsAlreadyBeenUsed] = useState<boolean>(false);

  const checkPreviousPasswordsDefer = async () => {
    let req: CheckPreviousPasswordModel = {
      userId: userState?.user?.id ?? -1,
      newPassword: newPw
    };

    return checkPreviousPasswords(req);
  }

  const checkPreviousPasswordsRequest = useAsync({
    deferFn: checkPreviousPasswordsDefer,
    onResolve: (response: DataResponse<boolean>) => {
      setIsAlreadyBeenUsed(response.data);
    },
  });

  // Data
  const save = useAsync({
    deferFn: async () => {
      await updateMyPassword({
        currentPassword: currentPw,
        newPassword: newPw,
        confirmPassword: confirmPw,
      });
    },
  });

  // Handlers
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.run();
  };

  const rules: Rule[] = [
    {
      text: 'Between 8 and 20 characters',
      validation: () => {
        return newPw.length >= 8 && newPw.length <= 20;
      },
    },
    {
      text: 'Has uppercase character',
      validation: () => {
        return /[A-Z]/.test(newPw);
      },
    },
    {
      text: 'Has lowercase character',
      validation: () => {
        return /[a-z]/.test(newPw);
      },
    },
    {
      text: 'Has a digit',
      validation: () => {
        return /[0-9]/.test(newPw);
      },
    },
    {
      text: 'Has special character',
      validation: () => {
        //eslint-disable-next-line
        return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(newPw);
      },
    },
    {
      text: 'Password & Confirm Password Match',
      validation: () => {
        return newPw === confirmPw && newPw.length > 0 && confirmPw.length > 0;
      },
    },
    {
      text: 'Password cannot be one of the previous 4 passwords',
      validation: () => {
        return !isAlreadyBeenUsed && newPw.length > 8;
      },
    },
  ];

  // Validation Rules
  const isValid =
    !rules.some((r) => r.validation() === false) && currentPw.length > 0;

  return (
    <Layout>
      <Fragment>
        <ResponseStatus state={save} />
        <form onSubmit={handleSubmit}>
          <div className='grid grid-cols-6 gap-6 mb-8'>
            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                label='Current Password'
                name='current-password'
                minLength={1}
                value={currentPw}
                type='password'
                onChange={(e) => setcurrentPw(e.target.value)}
              />
            </div>

            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                label='New Password'
                name='new-password'
                minLength={1}
                value={newPw}
                type='password'
                onChange={(e) => setNewPw(e.target.value)}
                onBlur={() => checkPreviousPasswordsRequest.run()}
              />
            </div>

            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                label='Confirm Password'
                name='confirm-password'
                minLength={1}
                value={confirmPw}
                type='password'
                onChange={(e) => setConfirmPw(e.target.value)}
              />
            </div>

            <div className='col-span-6 sm:col-span-6'>
              <ol>
                {rules.map((r) => (
                  <li key={r.text} className='flex items-center mb-1'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className={cx(
                        'h-6 w-6 mr-1',
                        r.validation() === true
                          ? 'text-ptsgreen-normal'
                          : 'text-gray-400'
                      )}
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span className='text-sm'>{r.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className='text-right'>
            <SubmitButton
              text='Change Password'
              disabled={!isValid}
              spinning={save.isPending}
            />
          </div>
        </form>
      </Fragment>
    </Layout>
  );
};

export default ChangePassword;
